<template>
    <div class="CFormRadio">
        <div :class="[inline ? 'space-x-5' : 'space-y-2']">
            <div v-for="(option, index) in options" :key="option.id" :class="{ inline }">
                <label
                    class="items-center group w-full space-x-2"
                    :class="[
                        { 'block': fullWidth, 'inline-flex': !fullWidth, 'cursor-pointer': !(disabled || option.disabled) },
                        `direction-${direction}`
                    ]"
                >
                    <input
                        :ref="(el) => { if (el) radioRefs[index] = el as HTMLInputElement }"
                        type="radio"
                        class="input cursor-pointer"
                        :name="$attrs.name || 'radio-group'"
                        :value="option.value"
                        :disabled="disabled || option.disabled"
                        :required="required"
                        @change="handleChange(option.value)"
                    />

                    <!-- text label -->
                    <span
                        class="min-h-5 pt-px text-[0.96rem] leading-snug"
                        :class="{ 'opacity-50': disabled || option.disabled }"
                    >
                        <span v-if="htmlLabel" v-html="option.label" />
                        <template v-else>{{ option.label }}</template>
                    </span>
                    <div
                        v-if="option.description"
                        class="pl-6 text-sm text-gray-500 dark:text-txt-body"
                    >
                        {{ option.description }}
                    </div>
                </label>

                <!-- Extras outside the label -->
                <div v-if="option.payInfo || option.manageLink || option.tooltip" class="flex items-center gap-2">
                    <CBadge v-if="option.payInfo" type="info" thin-text>{{ option.payInfo }}</CBadge>

                    <NuxtLink
                        v-if="option.manageLink"
                        class="text-blue-700 dark:text-blue-300 underline hover:no-underline"
                        :to="{
                            name: 'profile-orgs-edit-id',
                            params: { id: String(option.value), backLink: 'profile-jobs-create' }
                        }"
                    >
                        Zmeniť
                    </NuxtLink>

                    <CTooltip v-if="option.tooltip">{{ option.tooltip }}</CTooltip>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, nextTick } from 'vue'
import CBadge from '@/components/ui/CBadge.vue'
import CTooltip from '@/components/ui/CTooltip.vue'
import type { FormRadioOption as Option } from '#shared/types/FormRadioOption'

export type { FormRadioOption as Option } from '#shared/types/FormRadioOption'

type Direction = 'row' | 'col'

const props = defineProps({
    modelValue: {
        type: [String, Number, Boolean],
        default: undefined
    },
    options: {
        type: Array as PropType<Option[]>,
        required: true
    },
    inline: {
        type: Boolean
    },
    hideBullets: {
        type: Boolean
    },
    required: {
        type: Boolean
    },
    disabled: {
        type: Boolean
    },
    htmlLabel: {
        type: Boolean
    },
    fullWidth: {
        type: Boolean
    },
    direction: {
        type: String as PropType<Direction>,
        default: 'row'
    }
})

const emit = defineEmits(['update:modelValue'])

// Store refs to radio inputs
const radioRefs = ref<HTMLInputElement[]>([])

// Update checked state of all radios
// Note: We use a class toggle because CSS :checked pseudo-selector
// doesn't update reactively when setting .checked via JavaScript
function updateCheckedState() {
    nextTick(() => {
        setTimeout(() => {
            radioRefs.value.forEach((radio, index) => {
                if (radio && props.options[index]) {
                    const shouldBeChecked = props.modelValue === props.options[index].value
                    radio.checked = shouldBeChecked
                    radio.classList.toggle('is-checked', shouldBeChecked)
                }
            })
        }, 50)
    })
}

// Watch for modelValue changes
watch(() => props.modelValue, () => {
    updateCheckedState()
}, { immediate: true })

// Also update on mount
onMounted(() => {
    updateCheckedState()
})

function handleChange(value: string | number | boolean) {
    emit('update:modelValue', value)
    // Immediately update visual state for all radios
    radioRefs.value.forEach((radio, index) => {
        if (radio && props.options[index]) {
            const shouldBeChecked = value === props.options[index].value
            radio.classList.toggle('is-checked', shouldBeChecked)
        }
    })
}
</script>
